import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { CardSplit, CardPaymentKind } from './types';

export interface CardSplitWithMachine extends CardSplit {
  machine: { id: string; name: string; operator: string | null } | null;
}

export function useCardSplits() {
  const { clientId } = useCurrentClient();
  return useQuery({
    queryKey: ['card_splits', clientId],
    enabled: !!clientId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<CardSplitWithMachine[]> => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('card_splits')
        .select('*, machine:card_machines(id, name, operator)')
        .eq('client_id', clientId)
        .order('transaction_date', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as CardSplitWithMachine[];
    },
  });
}

export interface CreateCardSplitInput {
  cardMachineId: string;
  description?: string;
  transactionDate: string;
  installments: number;
  paymentKind: CardPaymentKind;
  grossAmount: number;
  mdrPct: number;
  mdrAmount: number;
  anticipationRateMonthlyPct: number;
  anticipationAmount: number;
  netAmount: number;
  accountId?: string | null;
  notes?: string;
  registerAsTransaction?: boolean;
  categoryId?: string | null;
  costCenterId?: string | null;
}

export function useCreateCardSplit() {
  const qc = useQueryClient();
  const { clientId } = useCurrentClient();

  return useMutation({
    mutationFn: async (input: CreateCardSplitInput) => {
      if (!clientId) throw new Error('Cliente não selecionado');

      let transactionId: string | null = null;
      if (input.registerAsTransaction && input.accountId && input.netAmount > 0) {
        const { data: tx, error: txErr } = await supabase
          .from('transactions')
          .insert({
            client_id: clientId,
            type: 'income',
            description: input.description?.trim() || `Recebimento cartão`,
            amount: input.netAmount,
            date: input.transactionDate,
            account_id: input.accountId,
            category_id: input.categoryId ?? null,
            cost_center_id: input.costCenterId ?? null,
            status: 'completed',
            payment_date: input.transactionDate,
            notes: input.notes?.trim() || null,
          })
          .select()
          .single();
        if (txErr) throw txErr;
        transactionId = (tx as { id: string }).id;
      }

      const { error } = await supabase.from('card_splits').insert({
        client_id: clientId,
        card_machine_id: input.cardMachineId,
        description: input.description?.trim() || null,
        transaction_date: input.transactionDate,
        installments: input.installments,
        payment_kind: input.paymentKind,
        gross_amount: input.grossAmount,
        mdr_pct: input.mdrPct,
        mdr_amount: input.mdrAmount,
        anticipation_rate_monthly_pct: input.anticipationRateMonthlyPct,
        anticipation_amount: input.anticipationAmount,
        net_amount: input.netAmount,
        account_id: input.accountId ?? null,
        transaction_id: transactionId,
        notes: input.notes?.trim() || null,
      });
      if (error) {
        if (transactionId) {
          await supabase
            .from('transactions')
            .update({ deleted_at: new Date().toISOString() })
            .eq('id', transactionId);
        }
        throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['card_splits'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['timeline'] });
      toast.success('Antecipação registrada.');
    },
    onError: (e: Error) => toast.error(`Erro ao registrar: ${e.message}`),
  });
}

export function useDeleteCardSplit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (split: CardSplit) => {
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from('card_splits')
        .update({ deleted_at: nowIso })
        .eq('id', split.id);
      if (error) throw error;
      if (split.transaction_id) {
        await supabase
          .from('transactions')
          .update({ deleted_at: nowIso })
          .eq('id', split.transaction_id);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['card_splits'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      toast.success('Antecipação excluída.');
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}
